import dynamic from 'next/dynamic'

import { useCommonServices } from '@lib/hooks'
import { withRequestParsing } from '@lib/mixins/with-request-parsing'
import { type BasePageProps, withServices } from '@lib/mixins/with-services'
import { withTelemetry } from '@lib/mixins/with-telemetry'

import type { TypePageEntry } from '@contentful-types/page'
import type { TypeSeoMetadataEntry } from '@contentful-types/seo-metadata'
import type { TypeSocialMetadataEntry } from '@contentful-types/social-metadata'

import { IS_BROWSER } from '@constants/globals'

import { getHomepageHeroSeed } from '@components/page-head/utils'

import { isCustomPage } from '@custom/registry/utils'

const DynamicCustomPage = dynamic(() => import('@custom/base/custom-page'))
const DynamicCustom500 = dynamic(() => import('./500'))
const DynamicCmsPage = dynamic(() => import('@components/page-core/page'))

interface RenderProps extends BasePageProps {
  page?: TypePageEntry
  pageSlug?: string
  heroImageSeed?: number | null
  queryParams?: Record<string, string | string[] | undefined>
  metadata: {
    social?: TypeSocialMetadataEntry
    seo?: TypeSeoMetadataEntry
    enabledFlags?: string[]
  }
}

const Render = withServices(
  withTelemetry(({ page, pageSlug, heroImageSeed, metadata }: RenderProps) => {
    const { navigationService } = useCommonServices()

    if (navigationService.currentQueryParams.debugClientError === 'true' && IS_BROWSER) {
      throw new Error('Test error')
    }

    const useCustomPage = pageSlug && isCustomPage(pageSlug)
    const useErrorPage = !page || pageSlug === '/500'

    // Check if this page should use custom rendering
    if (useCustomPage) {
      return (
        <DynamicCustomPage
          slug={pageSlug}
          metadata={metadata}
          heroImageSeed={heroImageSeed}
          enabledFlags={metadata.enabledFlags}
        />
      )
    }

    // Handle regular CMS page rendering
    if (useErrorPage) {
      return <DynamicCustom500 />
    }

    return <DynamicCmsPage data={page} metadata={metadata} />
  }),
)

export const getServerSideProps = withRequestParsing<RenderProps>(
  async ({ requestData, context }) => {
    if (context.query.debugRenderError === 'true') {
      throw new Error('Test error')
    }

    return {
      page: requestData?.pageEntry,
      pageSlug: requestData?.pageSlug,
      heroImageSeed: getHomepageHeroSeed(requestData?.pageSlug),
      queryParams: context.query,
      metadata: {
        ...(requestData?.metadata?.social && { social: requestData.metadata.social }),
        ...(requestData?.metadata?.seo && { seo: requestData.metadata.seo }),
        ...(requestData?.metadata?.enabledFlags && {
          enabledFlags: requestData.metadata.enabledFlags,
        }),
      },
    }
  },
)

export default Render
